import { forwardRef, useEffect } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IconCornerDownRight } from 'components/UI/Icons/CornerDownRight';
import { Button } from 'components/UI/Tailwind/Button';
import { Input } from 'components/UI/Tailwind/Input';
import type { Label } from 'components/UI/Tailwind/Label';
import { TagList } from 'components/UI/Tailwind/TagList';

type TInput = JSX.IntrinsicElements['input'];

export interface FormInput
  extends
    Omit<TInput, 'name' | 'onChange' | 'onReset' | 'prefix' | 'ref' | 'suffix'>,
    Omit<Label, 'variant'>,
    Pick<Input, 'onChange' | 'variant'>,
    Pick<UseControllerProps, 'name'> {
  error?: string;
  hideError?: boolean;
  hint?: string;
  loading?: boolean;
  onSuffixClick?: JSX.IntrinsicElements['button']['onClick'];
  prefix?: JSX.Element | string;
  showAddButton?: boolean;
  showErrorMessages?: boolean;
  suffix?: JSX.Element | string;
  suffixTagName?: JSX.ElementType;
}

export const FormInput = forwardRef<HTMLInputElement, FormInput>(
  (
    {
      error,
      hideError,
      hint,
      multiple,
      name,
      showAddButton,
      showErrorMessages,
      ...props
    },
    ref,
  ) => {
    const { formState, register, setValue } = useFormContext();

    const { t } = useTranslation();

    const watched = useWatch({ disabled: !multiple, name });
    const watchValue: string[] = multiple ? watched : [];

    const addTag = (value: string) => {
      if (value && !watchValue.includes(value)) {
        setValue(name, [...watchValue, value]);
      }
    };

    const deleteTag: TagList['onDeleteTag'] = (index) => {
      setValue(name, [
        ...watchValue.slice(0, index),
        ...watchValue.slice(index + 1),
      ]);
    };

    const onKeyDown: FormInput['onKeyDown'] = (event) => {
      if (event.key === 'Enter' && multiple) {
        event.preventDefault();
        const target = event.target as HTMLInputElement;
        addTag(target.value.trim());
        target.value = '';
      }
    };

    const onPressAdd: Extract<
      React.ComponentProps<typeof Button>['onClick'],
      React.PointerEventHandler<HTMLButtonElement>
    > = (event) => {
      const target = event.currentTarget?.previousSibling as HTMLInputElement;
      if (target.value) {
        addTag(target.value.trim());
        target.value = '';
      }
    };

    useEffect(() => {
      if (multiple) {
        // manually register the field when not in multi-select mode
        register(name);
      }
    }, [multiple, name, register]);

    const field = register(name);

    const nestedError =
      typeof name === 'string' && name.includes('.')
        ? (name
            .split('.')
            .reduce<unknown>(
              (obj, key) =>
                obj != null && typeof obj === 'object' && key in obj
                  ? (obj as Record<string, unknown>)[key]
                  : undefined,
              formState.errors,
            ) as { message?: string } | undefined)
        : (formState.errors?.[name as keyof typeof formState.errors] as
            | { message?: string }
            | undefined);
    const errorMessage = error ?? nestedError?.message;

    return (
      <Input
        {...props}
        {...(!multiple ? field : {})}
        disabled={field.disabled || props.disabled || formState.isSubmitting}
        // @ts-expect-error
        error={!hideError && errorMessage}
        multiple={multiple}
        hint={hint}
        onKeyDown={onKeyDown}
        showErrorMessages={showErrorMessages}
        {...(multiple && showAddButton
          ? {
              buttonEnd: (
                <Button
                  iconStart={<IconCornerDownRight />}
                  onClick={onPressAdd}
                  variant="default-secondary"
                  size={26}
                  className="relative right-1.5"
                >
                  <span>{t('share.add')}</span>
                </Button>
              ),
            }
          : {})}
        ref={(node) => {
          if (ref) {
            if (typeof ref === 'function') {
              ref(node);
            } else {
              ref.current = node;
            }
          }

          if (!multiple) {
            field.ref(node);
          }
        }}
        onBlur={(...args) => {
          field.onBlur(...args);
          props.onBlur?.(...args);
        }}
        onChange={(...args) => {
          field.onChange(...args);
          props.onChange?.(...args);
        }}
      >
        {multiple && (
          <TagList
            className="mt-1"
            editable
            onDeleteTag={deleteTag}
            placeholder={t('layout.noneSelected')}
            tags={watchValue}
          />
        )}
      </Input>
    );
  },
);
